import React, { useState, useCallback, useMemo } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerClusterer,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import ScamActivityIncidentCard from "./ScamActivityIncidentCard";
import "./css/ScamActivityMap.css";

const LIBRARIES = ["places", "visualization"];

const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };

// Pin colours by scam type
const PIN_COLORS = {
  "Romance Scam": "#e91e8c",
  Phishing: "#f44336",
  "Tech Support Scam": "#ff9800",
  "Investment Scam": "#9c27b0",
  "Employment Scam": "#2196f3",
  "Online Shopping Scam": "#00bcd4",
  "Government Impersonation": "#795548",
  "Phone Scam": "#607d8b",
  "Lottery Scam": "#ffc107",
  "Charity Scam": "#4caf50",
  Other: "#757575",
};

function pinIcon(scamType) {
  const color = PIN_COLORS[scamType] || "#757575";
  // SVG circle marker — colour-coded AND shape-coded for accessibility
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
      <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 22 14 22S28 24.5 28 14C28 6.27 21.73 0 14 0z"
            fill="${color}" stroke="#fff" stroke-width="2"/>
      <circle cx="14" cy="14" r="6" fill="#fff" fill-opacity="0.85"/>
    </svg>`;
  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: { width: 28, height: 36 },
  };
}

/**
 * Google Maps view for the Scam Activity Map.
 *
 * @param {Object[]} incidents     - ScamIncidentDTO[]
 * @param {Object}   searchCenter  - {lat, lng}
 * @param {number}   radiusMiles   - Used to set initial zoom
 */
function ScamActivityMapView({ incidents, searchCenter, radiusMiles }) {
  const [selectedIncident, setSelectedIncident] = useState(null);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries: LIBRARIES,
  });

  // Choose zoom level based on radius
  const initialZoom = useMemo(() => {
    if (radiusMiles <= 5) return 13;
    if (radiusMiles <= 10) return 12;
    if (radiusMiles <= 25) return 11;
    return 10;
  }, [radiusMiles]);

  const onMarkerClick = useCallback((incident) => {
    setSelectedIncident(incident);
  }, []);

  const onMapClick = useCallback(() => {
    setSelectedIncident(null);
  }, []);

  if (loadError) {
    return (
      <div className="sam-map-error">
        <p>⚠️ Could not load the map. Please refresh and try again.</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="sam-map-loading">
        <div className="sam-map-loading__spinner" />
        <p>Loading map…</p>
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div className="sam-map-error">
        <p>⚠️ Google Maps API key is not configured.</p>
      </div>
    );
  }

  return (
    <div className="sam-map-container">
      {/* ── Legend ── */}
      <div className="sam-map-legend">
        {Object.entries(PIN_COLORS)
          .filter(([type]) => incidents?.some((i) => i.scamType === type))
          .map(([type, color]) => (
            <span key={type} className="sam-map-legend__item">
              <span
                className="sam-map-legend__dot"
                style={{ backgroundColor: color }}
              />
              {type}
            </span>
          ))}
      </div>

      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={searchCenter}
        zoom={initialZoom}
        onClick={onMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          zoomControl: true,
        }}
      >
        {incidents && incidents.length > 0 && (
          <MarkerClusterer>
            {(clusterer) =>
              incidents.map((incident) => (
                <Marker
                  key={incident.id}
                  position={{ lat: incident.latitude, lng: incident.longitude }}
                  icon={pinIcon(incident.scamType)}
                  clusterer={clusterer}
                  onClick={() => onMarkerClick(incident)}
                  title={`${incident.scamType} — ${incident.city}, ${incident.state}`}
                />
              ))
            }
          </MarkerClusterer>
        )}

        {/* Search-centre marker (home pin) */}
        {searchCenter && (
          <Marker
            position={searchCenter}
            icon={{
              url: "data:image/svg+xml;charset=UTF-8," +
                encodeURIComponent(
                  `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                    <circle cx="16" cy="16" r="14" fill="#1a73e8" stroke="#fff" stroke-width="3"/>
                    <circle cx="16" cy="16" r="5" fill="#fff"/>
                  </svg>`
                ),
              scaledSize: { width: 32, height: 32 },
            }}
            title="Your search location"
            zIndex={1000}
          />
        )}

        {/* Info window for selected incident */}
        {selectedIncident && (
          <InfoWindow
            position={{
              lat: selectedIncident.latitude,
              lng: selectedIncident.longitude,
            }}
            onCloseClick={() => setSelectedIncident(null)}
          >
            <ScamActivityIncidentCard
              incident={selectedIncident}
              compact={true}
            />
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
}

export default ScamActivityMapView;
